import { useCallback, useEffect, useRef, useState } from 'react';
import { useRevalidator } from 'react-router';
import { startAuthentication } from '@simplewebauthn/browser';

import { useTracker } from '@archipro-website/tracker';
import { useAtomValue } from 'jotai';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { cn } from '@rocco/utils/cn';
import type { AuthSource } from '~/modules/user/type';
import { PASSKEY_LOGIN_OPTIONS_API_ROUTE } from '~/routes/remix-api.user.passkey-login-options';
import { PASSKEY_LOGIN_VERIFY_API_ROUTE } from '~/routes/remix-api.user.passkey-login-verify';
import { derivePlatformBiometricLabel } from '~/modules/user/passkey/platform-label';
import { startMinimumHold } from '~/modules/user/passkey/minimum-hold';
import { forgetPasskeyEnrolment } from '~/modules/user/passkey/passkey-device-hint';
import {
    logPasskeySuppressed,
    passkeyDebugDetail,
} from '~/modules/user/passkey/passkey-diagnostics';
import {
    isTerminalRefusal,
    PasskeyRefusedError,
    refusalFromVerify,
} from '~/modules/user/passkey/passkey-login-failure';
import type { PasskeyLoginVerifyAction } from '~/routes/remix-api.user.passkey-login-verify';
import { getPasskeyIntentCopy, welcomeBackGreeting } from './auth-intent-copy';
import { GateHeading, GateSubcopy } from './GateHeading';
import { GateStickyFooter } from './GateStickyFooter';
import { SecureSignInGlyph } from './SecureSignInGlyph';

/**
 * Why the panel left step 0, which is all the host needs to word the screen it
 * lands on: someone who pressed "use another way" is not owed an explanation,
 * and a device whose passkey has just been retired is owed a different one from
 * a device whose attempt merely failed.
 */
export type PasskeyLoginFallbackReason =
    | 'user-choice'
    | 'credential-retired'
    | 'attempt-failed';

export interface PasskeyLoginStepProps {
    /** Host surface, attributed on the funnel events. */
    authSource: AuthSource;
    /** Display name from the device hint for the greeting. */
    firstName?: string;
    /** The assertion verified and the web session is established. */
    onSuccess: () => void;
    /** Step aside to the regular auth step 0 (refusal or user choice). */
    onFallback: (reason: PasskeyLoginFallbackReason) => void;
    /**
     * Refresh loader data on success. In-place hosts (enquiry gate) need it —
     * the raw-fetch verify bypasses Remix's revalidation; redirect hosts skip
     * it because their full-page reload refreshes every loader anyway.
     */
    refreshOnSuccess?: boolean;
    className?: string;
}

const GENERIC_FAILURE =
    'Secure Sign-In failed. Please try another way to log in.';

/** Every ceremony animates for at least this long, however fast the API is. */
const MIN_CEREMONY_MS = 350;

/**
 * Step-0 passkey login (BC-4832): shown instead of the auth modal's initial
 * step when this device holds an enrolled-passkey hint. Mounting immediately
 * runs the discoverable-credential ceremony (the platform sheet appears
 * without an extra tap where the browser allows it); a dismissed prompt drops
 * to an explicit "Log in with Face ID" button, and a server refusal falls
 * back to the regular step silently.
 */
export const PasskeyLoginStep = ({
    authSource,
    firstName,
    onSuccess,
    onFallback,
    refreshOnSuccess,
    className,
}: PasskeyLoginStepProps) => {
    const tracker = useTracker();
    const revalidator = useRevalidator();
    // The glyph sits directly above the pinned actions on the sheet, where
    // the desktop card's flexible spacer collapses to nothing.
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    /**
     * A ceremony outlives the screen that started it: the platform sheet can be
     * answered after the modal has closed. Nothing that changes state the reader
     * would need to be told about may run once that has happened — least of all
     * forgetting the passkey, which they would discover only by its absence.
     */
    const mountedRef = useRef(true);
    useEffect(
        () => () => {
            mountedRef.current = false;
        },
        [],
    );
    const [busy, setBusy] = useState(true);
    const [succeeded, setSucceeded] = useState(false);
    const [error, setError] = useState<string>();

    const platformLabel = derivePlatformBiometricLabel();

    /**
     * Report a ceremony that did not log anyone in.
     *
     * A dismissed prompt is not one of these. Safari answers the automatic
     * attempt with a refusal on every visit, so counting it would drown the
     * signal this event exists to carry — how often a device is holding a
     * passkey the backend will not take.
     */
    const trackFailure = useCallback(
        (outcome: 'credential_rejected' | 'attempt_failed') => {
            tracker.log('PasskeyLoginFailed', {
                url: new URL(window.location.href),
                data: { Source: authSource, ExtraData: outcome },
            });
        },
        [tracker, authSource],
    );

    const runCeremony = useCallback(
        async (auto: boolean) => {
            setBusy(true);
            setError(undefined);
            const settle = startMinimumHold(MIN_CEREMONY_MS);
            try {
                const optionsResponse = await fetch(
                    PASSKEY_LOGIN_OPTIONS_API_ROUTE,
                    { method: 'POST' },
                );
                const options = (await optionsResponse.json()) as
                    | { ok: true; optionsJSON: string }
                    | { ok: false; message: string };
                if (!options.ok) {
                    throw new Error(options.message);
                }

                const assertion = await startAuthentication({
                    optionsJSON: JSON.parse(options.optionsJSON),
                });

                const verifyBody = new FormData();
                verifyBody.append('credential', JSON.stringify(assertion));
                const verifyResponse = await fetch(
                    PASSKEY_LOGIN_VERIFY_API_ROUTE,
                    { method: 'POST', body: verifyBody },
                );
                const verified =
                    (await verifyResponse.json()) as PasskeyLoginVerifyAction;
                if (!verified.ok) {
                    throw refusalFromVerify(verified);
                }
                if (!verified.sessionEstablished) {
                    // ok without a session (missing GraphQL data) — the login
                    // did not actually land; never complete the flow on it.
                    // A plain error, not a refusal: the server accepted the
                    // assertion, so it has said nothing against the credential.
                    throw new Error(GENERIC_FAILURE);
                }

                // No settle() here: the check-draw animation plays while the
                // host completes the login, so holding it back would only
                // serialise delay into the flow.
                setSucceeded(true);
                if (refreshOnSuccess) {
                    revalidator.revalidate();
                }
                onSuccess();
            } catch (err) {
                // Dismissed platform prompt — or the automatic attempt was
                // refused for lack of user activation. Either way the manual
                // button takes over.
                //
                // The hint is never touched here. Safari refuses every
                // automatic ceremony this way, so forgetting the passkey on
                // this branch would take the fast path off every Safari device
                // on its first visit.
                if (err instanceof Error && err.name === 'NotAllowedError') {
                    await settle();
                    setBusy(false);
                    return;
                }
                console.warn('[SecureSignIn] login failed', err);

                // The server has disowned the credential: no record of it, or a
                // record that can no longer resolve to a member. Retrying can
                // only reach the same answer, so the device forgets it — on the
                // manual attempt too, since leaving the button live under a
                // dead credential only buys a second doomed ceremony.
                if (isTerminalRefusal(err)) {
                    if (!mountedRef.current) return;
                    forgetPasskeyEnrolment();
                    logPasskeySuppressed(
                        'Passkey step 0',
                        'the server will not accept this credential, so this ' +
                            'device has forgotten it',
                    );
                    trackFailure('credential_rejected');
                    await settle();
                    onFallback('credential-retired');
                    return;
                }

                if (auto) {
                    // The automatic attempt failed before the user chose
                    // anything — step aside to the regular flow rather than
                    // surfacing noise. The passkey stays: nothing here says it
                    // is the credential that is wrong.
                    trackFailure('attempt_failed');
                    await settle();
                    onFallback('attempt-failed');
                    return;
                }

                trackFailure('attempt_failed');
                const isServerMessage =
                    (err instanceof PasskeyRefusedError ||
                        (err instanceof Error && err.name === 'Error')) &&
                    err.message.trim() !== '' &&
                    // The live error filter answers anything it will not pass
                    // through with this exact word. It is a marker for us, not a
                    // sentence for a reader.
                    err.message !== 'ERROR';
                await settle();
                setError(
                    isServerMessage
                        ? err.message
                        : (passkeyDebugDetail(err) ?? GENERIC_FAILURE),
                );
                setBusy(false);
            }
        },
        [onSuccess, onFallback, refreshOnSuccess, revalidator, trackFailure],
    );

    const startedRef = useRef(false);
    useEffect(() => {
        if (startedRef.current) return;
        startedRef.current = true;
        tracker.log('PasskeyLoginPromptShown', {
            url: new URL(window.location.href),
            data: { Source: authSource },
        });
        void runCeremony(true);
    }, [runCeremony, tracker, authSource]);

    return (
        <div
            className={cn(
                'mx-auto flex min-h-0 w-full max-w-90.75 flex-1 flex-col',
                className,
            )}
        >
            <GateHeading className="mt-3">
                {welcomeBackGreeting(firstName)}
            </GateHeading>
            <GateSubcopy className="leading-normal">
                {/* The gate's intent, then the enrolment ask's speed promise —
                    step 0 has no frame of its own, so the promise is borrowed
                    rather than inventing a second voice for the same feature. */}
                {getPasskeyIntentCopy(authSource)}
            </GateSubcopy>
            <div className={cn('mt-10.5', isMobile && 'mb-10')}>
                <SecureSignInGlyph
                    state={succeeded ? 'success' : busy ? 'busy' : 'idle'}
                />
            </div>
            {error && (
                <p
                    role="alert"
                    className="mt-4 text-center text-[0.8125rem] text-destructive"
                >
                    {error}
                </p>
            )}
            <div className="flex-1" />
            <GateStickyFooter desktopClassName="mb-9">
                <button
                    type="button"
                    disabled={busy}
                    onClick={() => void runCeremony(false)}
                    className="flex h-14 w-full items-center justify-center rounded-[1.375rem] bg-[#323232] text-[1.125rem] font-medium tracking-[-0.02em] text-[#f3f3f3] shadow-[0_0.1875rem_0.8125rem_0_rgba(0,0,0,0.25),0_1.25rem_2.125rem_0_rgba(0,0,0,0.25)] transition-colors hover:bg-[#2c2c2c] disabled:cursor-not-allowed"
                >
                    {succeeded
                        ? 'Logged in.'
                        : busy
                          ? 'Authenticating.'
                          : `Log in with ${platformLabel ?? 'Secure Sign-In'}`}
                </button>
                <button
                    type="button"
                    disabled={busy}
                    onClick={() => onFallback('user-choice')}
                    className="mt-6 w-full text-center text-[1.0625rem] tracking-[-0.02em] text-[#44443f]"
                >
                    Use another way to log in
                </button>
            </GateStickyFooter>
        </div>
    );
};
